import type { RichTextDoc } from '@bcecloud/portal-ui';
import type { OrganizationAgreement } from '@/lib/agreements';
import type { DevFeature, DevStage } from '@/lib/dev-tracker';
import type { EventCsr, EventDetail, EventRecap } from '@/lib/events';
import type { ProgramPairing } from '@/lib/program-pairings';
import type { PromoBanner } from '@/lib/promo-banners';
import type { ResourceDetail, ResourceTopic } from '@/lib/resources';
import type { SeasonTag } from '@/lib/season-tags';
import type { LinkedSalesforceAccounts, Member, OrganizationDetail, OrganizationSummary } from '@/lib/types';
import type { CatalogueRow, CatalogueableProgram, EnquiryRow, LedgerRow, OrgCredits, OrgPortalSettings } from '@/pages/OrganizationDetailPage';
import type { ConciergeQuestionRow } from '@/pages/org-questions-tab';

/**
 * The client portal side of the sandbox: Northwind Group, the fictional
 * client the portal guide is written around (names and copy from the API's
 * `scripts/northwind-demo` fixtures), with the curated content Roy manages
 * in the admin app. Hand-written, never captured: nothing here is a real
 * client. Dates are relative to when the sandbox starts, so live banners
 * stay live and the delivered event stays in the past.
 */

export const PORTAL_SEED_VERSION = 1;

/** One program in BCE's catalog, as the portal content refers to it. */
export interface CatalogProgram {
  readonly id: string;
  readonly name: string;
}

/** A sandbox feature's season tags, kept apart so `programCount` can be counted. */
export interface SandboxSeasonTag extends Omit<SeasonTag, 'programCount' | 'monthsLabel'> {
  readonly programIds: readonly string[];
}

/** A resource with what the per-client tab counts. */
export interface SandboxResource extends ResourceDetail {
  readonly organizationViews: number;
  readonly lastOpenedAt: string | null;
}

/** An event the portal tours open: its detail, its charity side and its recaps. */
export interface PortalEvent {
  readonly detail: EventDetail;
  csr: EventCsr;
  recaps: EventRecap[];
}

export interface PortalStore {
  version: number;
  organizationId: string;
  /** Every client organization; only Northwind has its tabs filled in. */
  organizations: OrganizationDetail[];
  settings: OrgPortalSettings;
  credits: OrgCredits;
  ledger: LedgerRow[];
  catalogue: CatalogueRow[];
  library: CatalogueableProgram[];
  members: Member[];
  agreement: OrganizationAgreement;
  salesforce: LinkedSalesforceAccounts;
  messaging: Record<string, unknown>;
  inquiries: EnquiryRow[];
  concierge: ConciergeQuestionRow[];
  topics: ResourceTopic[];
  resources: SandboxResource[];
  banners: PromoBanner[];
  programs: CatalogProgram[];
  pairings: ProgramPairing[];
  seasonTags: SandboxSeasonTag[];
  features: DevFeature[];
  /** The delivered Bike Build, for donated items and facilitator feedback. */
  eventId: string;
  events: PortalEvent[];
}

const NORTHWIND_ID = '6e0a1c4f2b8d4a17c3e9f001';
const BLUEBIRD_ID = '6e0a1c4f2b8d4a17c3e9f002';
const HARBOR_ID = '6e0a1c4f2b8d4a17c3e9f003';
export const PORTAL_EVENT_ID = '6e0a1c4f2b8d4a17c3e9e604';
const PORTAL_DOMAIN = 'portal.example';

/** Days from the sandbox's start, as `YYYY-MM-DD`. */
function day(now: Date, offset: number): string {
  return new Date(now.getTime() + offset * 86_400_000).toISOString().slice(0, 10);
}

/** Days from the sandbox's start, as a timestamp. */
function at(now: Date, offset: number): string {
  return new Date(now.getTime() + offset * 86_400_000).toISOString();
}

function idFor(prefix: string, index: number): string {
  return `${prefix}${String(index).padStart(24 - prefix.length, '0')}`;
}

/** An organization's detail record with nothing set up yet. */
export function detailOf(summary: OrganizationSummary): OrganizationDetail {
  const { brand: _brand, memberCount, ...rest } = summary;
  return { ...rest, clerkOrgId: `org_sandbox_${summary.slug}`, brand: null, teams: [], _count: { memberships: memberCount, bookingRequests: 0 }, salesforceAccountIds: [], salesforceParentAccountIds: [] };
}

/** The list row for an organization. */
export function summaryOf(detail: OrganizationDetail): OrganizationSummary {
  const { brand, clerkOrgId: _clerk, teams: _teams, _count, salesforceAccountIds: _accounts, salesforceParentAccountIds: _parents, partner: _partner, ...rest } = detail;
  return { ...rest, memberCount: _count.memberships, brand: brand ? { logoUrl: brand.logoUrl, primaryColor: brand.primaryColor } : null };
}

/** The programs in Northwind's catalog, as the demo seeds them. */
const CATALOG_NAMES = [
  'Bike Build Donation®',
  'Competition to Collaboration®',
  'Best Corporate Feud',
  'Igniting Team Performance®',
  'Speed Networking',
  'Team Olympics',
  'Bears and Blankets',
  'Operation Military Care',
  'Amazing Chase SmartHunt®',
  'Crack the Case!®',
  'Momentum',
  'Food Truck Face-Off',
  'Gingerbread Architect',
  'Presentation Skills Training',
  'Virtual Escape Room: Escape the Office',
  'STEM Backpacks for Kids',
] as const;

/** Programs in the library that Northwind has not added yet. */
const MORE_PROGRAMS = ['Build-a-Guitar®', 'City SmartHunt®', 'Go-Kart Competition', 'Helping the Homeless', 'Bridge to the Future'] as const;

function programsList(): CatalogProgram[] {
  return [...CATALOG_NAMES, ...MORE_PROGRAMS].map((name, index) => ({ id: idFor('6e0b', index + 1), name }));
}

function programId(programs: readonly CatalogProgram[], name: string): string {
  return programs.find((program) => program.name === name)?.id ?? '';
}

function article(...paragraphs: string[]): RichTextDoc {
  return { type: 'doc', content: paragraphs.map((text) => ({ type: 'paragraph' as const, content: [{ type: 'text' as const, text }] })) };
}

const TOPICS: readonly Pick<ResourceTopic, 'name' | 'icon' | 'description'>[] = [
  { name: 'Employee engagement', icon: 'sparkles', description: 'Research and ideas for keeping people connected to their work and each other.' },
  { name: 'Facilitation tips', icon: 'lightbulb', description: 'How to run the room: pacing, energy and getting every voice in.' },
  { name: 'Meeting openers', icon: 'mic', description: 'Short warm-ups that get a group talking before the agenda starts.' },
  { name: 'Debrief questions', icon: 'circle-help', description: 'Questions for the conversation after an event, when a team turns the day into something they keep.' },
  { name: 'CSR planning', icon: 'heart-handshake', description: 'Planning charity builds and giving programs with a real community partner.' },
  { name: 'Guides for event leaders', icon: 'book-marked', description: 'Checklists and guides for the people organizing an event inside their company.' },
  { name: 'Hybrid teams', icon: 'wifi', description: 'Keeping remote and in-office people connected between events.' },
];

export function topicId(index: number): string {
  return idFor('6e0c', index + 1);
}

function topicOf(name: string): { id: string; name: string } {
  const index = TOPICS.findIndex((topic) => topic.name === name);
  return { id: topicId(index), name };
}

/** The topic the resource tour files its new article under. */
export const EVENT_LEADERS_TOPIC_ID = topicOf('Guides for event leaders').id;

const NO_STATS = { views: 0, downloads: 0, linkOpens: 0 } as const;

function resources(now: Date): SandboxResource[] {
  const base = {
    slug: '',
    status: 'PUBLISHED',
    tags: [],
    audience: { scope: 'ALL_CLIENTS', organizations: [] },
    readingMinutes: null,
    file: null,
    link: null,
    body: null,
    publishProblems: [],
    organizationViews: 0,
    lastOpenedAt: null,
  } as const;
  const northwind = { id: NORTHWIND_ID, name: 'Northwind Group' };
  const rows: Omit<SandboxResource, 'id' | 'slug'>[] = [
    {
      ...base,
      title: 'Five meeting openers that get everyone talking',
      summary: 'Short warm-ups for the first ten minutes of a meeting, so the quiet people speak before the agenda starts.',
      format: 'ARTICLE',
      topic: topicOf('Meeting openers'),
      tags: ['warm-ups', 'meetings'],
      formatLine: 'Article · 3 min read',
      readingMinutes: 3,
      body: article('The first few minutes of a meeting decide who talks for the rest of it. These openers take five to ten minutes and need nothing but the people in the room.'),
      publishedAt: at(now, -5),
      createdAt: at(now, -9),
      updatedAt: at(now, -5),
      stats: { views: 64, downloads: 0, linkOpens: 0 },
    },
    {
      ...base,
      title: 'Debrief questions that make the day stick',
      summary: 'Questions for the half hour after a program, when a team turns a good afternoon into habits they keep.',
      format: 'ARTICLE',
      topic: topicOf('Debrief questions'),
      tags: ['debrief', 'reflection'],
      formatLine: 'Article · 4 min read',
      readingMinutes: 4,
      body: article('Ask what surprised people first. Then ask what they want to keep doing on Monday.'),
      publishedAt: at(now, -22),
      createdAt: at(now, -25),
      updatedAt: at(now, -22),
      stats: { views: 41, downloads: 0, linkOpens: 0 },
    },
    {
      ...base,
      title: 'Planning a charity build: the eight weeks before',
      summary: 'A week-by-week plan for a charity build, from choosing the partner to the handover on the day.',
      format: 'ARTICLE',
      topic: topicOf('CSR planning'),
      tags: ['charity', 'planning'],
      formatLine: 'Article · 5 min read',
      readingMinutes: 5,
      body: article('A charity build is a team program with a second audience: the people who receive what your team makes. Most of the planning is about that second audience.'),
      publishedAt: at(now, -60),
      createdAt: at(now, -62),
      updatedAt: at(now, -60),
      stats: { views: 37, downloads: 0, linkOpens: 0 },
    },
    {
      ...base,
      title: 'Event day checklist',
      summary: 'A printable checklist for the person running the room on the day: arrival, setup, the program and the wrap.',
      format: 'FILE',
      topic: topicOf('Guides for event leaders'),
      tags: ['checklist', 'printable'],
      formatLine: 'PDF · 2 pages',
      file: { name: 'Event day checklist.pdf', type: 'application/pdf', kind: 'PDF', size: 182_000, pageCount: 2 },
      publishedAt: at(now, -18),
      createdAt: at(now, -18),
      updatedAt: at(now, -18),
      stats: { views: 29, downloads: 22, linkOpens: 0 },
    },
    {
      ...base,
      title: 'Our charitable partners',
      summary: 'The nonprofits BCE builds with, and what each one does with what your team makes.',
      format: 'LINK',
      topic: topicOf('CSR planning'),
      tags: ['charity'],
      formatLine: 'Link · bestcorporateevents.com',
      link: { url: 'https://bestcorporateevents.com/about/charitable-partners', host: 'bestcorporateevents.com' },
      publishedAt: at(now, -75),
      createdAt: at(now, -75),
      updatedAt: at(now, -75),
      stats: { views: 0, downloads: 0, linkOpens: 18 },
    },
    {
      ...base,
      title: 'Founders Week: a checklist for office leads',
      summary: 'What each Northwind office lead does before, during and after Founders Week.',
      format: 'ARTICLE',
      topic: topicOf('Guides for event leaders'),
      tags: ['founders week', 'northwind'],
      formatLine: 'Article · 2 min read',
      readingMinutes: 2,
      audience: { scope: 'CHOSEN_CLIENTS', organizations: [northwind] },
      body: article('Book your office’s food truck slot, confirm the headcount two weeks out, and send BCE the Founders Week logo for the program materials.'),
      publishedAt: at(now, -3),
      createdAt: at(now, -4),
      updatedAt: at(now, -3),
      stats: { views: 12, downloads: 0, linkOpens: 0 },
      organizationViews: 12,
      lastOpenedAt: at(now, -1),
    },
    {
      ...base,
      title: 'Running a hybrid all-hands',
      summary: 'How to run an all-hands where the people on the call feel as present as the people in the room.',
      format: 'ARTICLE',
      topic: topicOf('Hybrid teams'),
      tags: ['hybrid', 'meetings'],
      formatLine: 'Article · 4 min read',
      readingMinutes: 4,
      audience: { scope: 'CHOSEN_CLIENTS', organizations: [{ id: BLUEBIRD_ID, name: 'Bluebird Financial' }] },
      body: article('Give the remote people a host of their own, and take their questions first.'),
      publishedAt: at(now, -14),
      createdAt: at(now, -16),
      updatedAt: at(now, -14),
      stats: { views: 9, downloads: 0, linkOpens: 0 },
    },
    {
      ...base,
      title: 'Pacing a 90-minute team program',
      summary: 'Where the energy dips in a 90-minute program, and what to do about it.',
      format: 'ARTICLE',
      topic: topicOf('Facilitation tips'),
      tags: ['facilitation', 'timing'],
      formatLine: 'Article · 3 min read',
      readingMinutes: 3,
      status: 'DRAFT',
      body: article('Plan a change of pace every twenty minutes.'),
      publishedAt: null,
      createdAt: at(now, -2),
      updatedAt: at(now, -2),
      stats: NO_STATS,
    },
  ];
  return rows.map((row, index) => ({ ...row, id: idFor('6e0d', index + 1), slug: row.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') }));
}

function members(): Member[] {
  const teams = {
    people: { id: idFor('6e0e', 1), name: 'People & Culture' },
    sales: { id: idFor('6e0e', 2), name: 'Sales' },
    engineering: { id: idFor('6e0e', 3), name: 'Engineering' },
    operations: { id: idFor('6e0e', 4), name: 'Operations' },
    community: { id: idFor('6e0e', 5), name: 'Community Impact' },
  } as const;
  type TeamKey = keyof typeof teams;
  const rows: readonly [string, string, string, Member['role'], readonly (readonly [TeamKey, 'TEAM_ADMIN' | 'MEMBER'])[], boolean?][] = [
    ['Hannah', 'Brooks', 'VP, People & Culture', 'ORG_ADMIN', []],
    ['Omar', 'Haddad', 'Director, Workplace Experience', 'ORG_ADMIN', []],
    ['Grace', 'Liu', 'People Programs Manager', 'MEMBER', [['people', 'TEAM_ADMIN'], ['community', 'MEMBER']]],
    ['Ryan', 'Castillo', 'Director, Sales Enablement', 'MEMBER', [['sales', 'TEAM_ADMIN']]],
    ['Priya', 'Nair', 'Engineering Program Manager', 'MEMBER', [['engineering', 'TEAM_ADMIN']]],
    ['Ben', 'Whitaker', 'Operations Manager', 'MEMBER', [['operations', 'TEAM_ADMIN']]],
    ['Alicia', 'Moreno', 'Community Impact Lead', 'MEMBER', [['community', 'TEAM_ADMIN']]],
    ['Jordan', 'Ellis', 'Account Executive', 'MEMBER', [['sales', 'MEMBER']]],
    ['Mei', 'Tanaka', 'Senior Software Engineer', 'MEMBER', [['engineering', 'MEMBER']]],
    ['Sam', 'Patel', 'HR Coordinator', 'MEMBER', [['people', 'MEMBER'], ['community', 'MEMBER']]],
    ['Leah', 'Fischer', 'Facilities Coordinator', 'MEMBER', [['operations', 'MEMBER']], true],
  ];
  return rows.map(([firstName, lastName, title, role, memberTeams, invited], index) => ({
    id: idFor('6e0f', index + 1),
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@northwind.example`,
    firstName,
    lastName,
    title,
    role,
    teams: memberTeams.map(([team, teamRole]) => ({ ...teams[team], role: teamRole })),
    creditApprovalLimit: firstName === 'Ryan' ? 150 : null,
    eventSizeLimit: null,
    isActive: true,
    isPending: invited === true,
    department: null,
    region: null,
  }));
}

function features(now: Date): DevFeature[] {
  const feature = (index: number, title: string, summary: string, area: DevFeature['area'], moves: readonly (readonly [DevStage, number])[]): DevFeature => {
    const history = moves.map(([stage, offset]) => ({ stage, on: day(now, offset), recordedBy: 'Morgan Reyes', recordedAt: at(now, offset) }));
    const last = history.at(-1);
    const stageDates: Partial<Record<DevStage, string>> = {};
    for (const event of history) stageDates[event.stage] = event.on;
    return {
      id: idFor('6e10', index),
      title,
      summary,
      area,
      stage: last?.stage ?? 'not_started',
      stageSince: last?.on ?? null,
      liveOn: stageDates.live ?? null,
      stageDates,
      history,
      links: [],
      createdAt: at(now, moves[0]?.[1] ?? 0),
    };
  };
  return [
    feature(1, 'Resource center', 'Guides, articles, files and links in every client portal, managed under Administration → Resources.', 'client_portal', [['in_development', -30], ['bce_review', -9]]),
    feature(2, 'Overview banners', 'Timely offers at the top of a client’s Overview, scheduled per client or for every portal.', 'client_portal', [['in_development', -40], ['bce_review', -25], ['approved', -18], ['live', -14]]),
    feature(3, 'Program pairings', 'Two programs BCE recommends together for an occasion, shown beside both programs in the portal.', 'client_portal', [['in_development', -35], ['bce_review', -20], ['revisions', -16]]),
    feature(4, 'Quarterly reports', 'A screen per quarter for each client, downloadable as a PDF.', 'client_portal', [['not_started', -10]]),
    feature(5, 'Donated items on events', 'What a charity build gave, item by item, adding to the client’s impact totals.', 'admin_app', [['in_development', -28], ['bce_review', -21], ['live', -12]]),
  ];
}

function bikeBuild(now: Date): PortalEvent {
  const eventDate = at(now, -120);
  const jamie = { _id: idFor('6e11', 1), firstName: 'Jamie', lastName: 'Ellison', email: 'jamie.ellison@example.com', phone: '(555) 010-0201' };
  const morgan = { _id: idFor('6e11', 2), firstName: 'Morgan', lastName: 'Reyes', email: 'morgan.reyes@example.com', phone: '(555) 010-0202' };
  const alicia = { name: 'Alicia Moreno', phone: '(555) 010-0310', email: 'alicia.moreno@northwind.example', role: 'Booking contact', isPrimary: true };
  const detail: EventDetail = {
    _id: PORTAL_EVENT_ID,
    account: { name: 'Northwind Group', type: 'Distribution' },
    accountName: 'Northwind Group',
    contact: alicia,
    contacts: [alicia],
    contactEmails: [alicia.email],
    startVenue: { name: 'Northwind Denver distribution center', street: '4800 Dahlia St', city: 'Denver', state: 'Colorado', postalCode: '80216' },
    users: [],
    charity: [{ name: 'Front Range Youth Cycling', type: 'Youth', charityRep: 'Dana Ortiz' }],
    opportunityName: 'NW-2604 / Northwind Group / Alicia Moreno / Denver / Bike Build Donation',
    programName: 'Bike Build Donation®',
    programDisplayName: 'Bike Build Donation®',
    salesperson: morgan,
    programManager: morgan,
    leadFacilitator: jamie,
    participants: 80,
    afterParticipants: '16 teams, 16 bikes with helmets and locks',
    contractNumber: 'NW-2604',
    stageName: 'Event Complete',
    eventDate,
    timeStartString: '1:00pm',
    timeEndString: '4:00pm',
    timeZone: 'MDT',
    longTimeZone: 'America/Denver',
    timeStart: eventDate,
    timeEnd: new Date(Date.parse(eventDate) + 3 * 3_600_000).toISOString(),
    recordings: [],
    localContacts: [],
    offersClientFeedback: true,
  };
  const csr: EventCsr = {
    organization: { id: NORTHWIND_ID, name: 'Northwind Group', slug: 'northwind' },
    charity: [{ name: 'Front Range Youth Cycling', type: 'Youth', charityRep: 'Dana Ortiz', deliveryContact: null }],
    plan: {
      beneficiaryName: 'Front Range Youth Cycling',
      cause: 'Bikes and safety gear for kids in north Denver',
      charityContactName: 'Dana Ortiz',
      communitiesSupported: 'Families near the Denver distribution center',
      donationStatus: 'DELIVERED',
      engagedMinutes: 150,
      thankYouMessage: 'Thank you, Northwind Group. Sixteen kids rode home on bikes your team built.',
      donationItems: [
        { id: 'bikes', description: 'Bikes, assembled and safety-checked', quantity: 16, kind: 'bicycles' },
        { id: 'helmets', description: 'Helmets, fitted on the day', quantity: 16 },
      ],
      donationCounts: { bicycles: 16 },
      updatedAt: at(now, -118),
    },
  };
  const recaps: EventRecap[] = [
    {
      _id: idFor('6e12', 1),
      event: PORTAL_EVENT_ID,
      facilitator: jamie,
      recap: [
        { name: 'went_well', question: 'What went well?', answer: 'Every team finished its bikes early and stayed for the handover.', type: 'comment' },
        { name: 'issues', question: 'Any issues on the day?', answer: 'The loading dock opened twenty minutes late. No effect on the program.', type: 'comment' },
      ],
      clientFeedback:
        'This was a strong build. Every team finished its bikes with time to spare, and the safety checks were careful. When the kids from Front Range Youth Cycling arrived, your people stayed to fit helmets and adjust seats, which is the part the families remember.',
      clientFeedbackAt: at(now, -119),
      createdAt: at(now, -119),
      updatedAt: at(now, -119),
    },
  ];
  return { detail, csr, recaps };
}

/**
 * Builds the portal seed.
 *
 * @param now - When the sandbox starts.
 * @returns A fresh portal store.
 */
export function portalSeed(now: Date): PortalStore {
  const programs = programsList();
  const portalUrl = (slug: string): string => `https://${slug}.${PORTAL_DOMAIN}`;
  const summary = (id: string, name: string, slug: string, memberCount: number, primaryColor: string, isDemo: boolean): OrganizationSummary => ({
    id,
    name,
    slug,
    customDomain: null,
    status: 'ACTIVE',
    createdAt: at(now, -300),
    memberCount,
    portalUrl: portalUrl(slug),
    brand: { logoUrl: null, primaryColor },
    kind: 'CLIENT',
    isDemo,
  });
  const northwindSummary = summary(NORTHWIND_ID, 'Northwind Group', 'northwind', 11, '#0b6e4f', true);
  const memberList = members();
  const catalogue: CatalogueRow[] = CATALOG_NAMES.map((name, index) => ({
    id: idFor('6e13', index + 1),
    v3ProgramId: programId(programs, name),
    name,
    imageUrl: null,
    isEnabled: true,
    isFeatured: index < 3,
    startingCredits: 60 + (index % 5) * 10,
    leadTimeDays: 21,
    requiresReview: name === 'STEM Backpacks for Kids',
    pricingTemplateId: null,
    pricingTemplateName: null,
    tiers: [],
    baseCredits: 60 + (index % 5) * 10,
    includedParticipants: 20,
    incrementSize: 10,
    incrementCredits: 8,
    ownLeadTimeDays: 21,
  }));
  const tag = (index: number, name: string, months: readonly number[], description: string, names: readonly string[]): SandboxSeasonTag => ({
    _id: idFor('6e14', index),
    name,
    months,
    description,
    isActive: true,
    programIds: names.map((programName) => programId(programs, programName)),
  });
  const pairing = (index: number, occasion: string, a: string, aLabel: string, b: string, bLabel: string, reason: string): ProgramPairing => ({
    id: idFor('6e15', index),
    occasion,
    reason,
    isActive: true,
    programA: { id: programId(programs, a), name: a, label: aLabel, isActive: true },
    programB: { id: programId(programs, b), name: b, label: bLabel, isActive: true },
    updatedAt: at(now, -30 - index),
  });
  const banner = (index: number, fields: Omit<PromoBanner, 'id' | 'overlapping' | 'status' | 'createdAt' | 'createdBy'>): PromoBanner => ({
    ...fields,
    id: idFor('6e16', index),
    overlapping: 0,
    status: 'LIVE',
    createdBy: 'Morgan Reyes',
    createdAt: at(now, -20 - index),
  });
  const northwindOnly = [{ id: NORTHWIND_ID, name: 'Northwind Group' }];

  return {
    version: PORTAL_SEED_VERSION,
    organizationId: NORTHWIND_ID,
    organizations: [
      {
          ...detailOf(northwindSummary),
        clerkOrgId: 'org_sandbox_northwind',
        brand: {
          id: idFor('6e17', 1),
          organizationId: NORTHWIND_ID,
          logoUrl: null,
          logoDarkUrl: null,
          primaryColor: '#0b6e4f',
          sidebarColor: '#0d3b2e',
          accentColor: '#f2a541',
          fontFamily: 'Arial, Helvetica, sans-serif',
          heroImageUrl: null,
          heroQuote: 'The best ideas at Northwind start when people from different teams get in a room together.',
          heroStatValue: null,
          heroStatLabel: null,
          loginEyebrow: 'NORTHWIND TEAM EVENTS',
          loginHeadline: 'Plan your next\nteam event here.',
          loginSubcopy: 'Book programs with your team credits, follow every event from request to delivery, and see what your teams gave back to the community.',
          welcomeMessage: 'Good to see you. Here is what your teams have coming up.',
        },
        teams: [...new Map(memberList.flatMap((member) => member.teams).map((team) => [team.id, { id: team.id, name: team.name }])).values()],
        _count: { memberships: memberList.length, bookingRequests: 18 },
        salesforceAccountIds: ['001NW00000DEMO1AAA'],
        salesforceParentAccountIds: [],
      },
      detailOf(summary(BLUEBIRD_ID, 'Bluebird Financial', 'bluebird', 34, '#1f5fa8', false)),
      detailOf(summary(HARBOR_ID, 'Harbor Light Media', 'harborlight', 19, '#7a3e9d', false)),
    ],
    settings: { conciergeEmail: null, conciergePhone: null, defaults: { email: 'concierge@bestcorporateevents.com', phone: '(800) 555-0150' } },
    credits: { balance: { available: 1_065, purchased: 2_000, reserved: 160, committed: 240, consumed: 535 } },
    ledger: [
      { id: idFor('6e18', 1), type: 'PURCHASE', amount: 1_600, reference: 'NW-PO-48213', reason: 'Annual agreement', actorName: 'Morgan Reyes', createdAt: at(now, -260) },
      { id: idFor('6e18', 2), type: 'PURCHASE', amount: 400, reference: 'NW-PO-51877', reason: 'Top-up for the fall', actorName: 'Morgan Reyes', createdAt: at(now, -100) },
      { id: idFor('6e18', 3), type: 'CONSUMPTION', amount: -90, reason: 'Bike Build Donation®, Denver', createdAt: at(now, -120) },
      { id: idFor('6e18', 4), type: 'CONSUMPTION', amount: -70, reason: 'Team Olympics, Minneapolis', createdAt: at(now, -75) },
    ],
    catalogue,
    library: MORE_PROGRAMS.map((name) => ({ v3ProgramId: programId(programs, name), name, category: null })),
    members: memberList,
    agreement: {
      agreement: {
        _id: idFor('6e19', 1),
        organizationId: NORTHWIND_ID,
        status: 'ACTIVE',
        termStart: day(now, -267),
        termEnd: day(now, 98),
        creditsPurchased: 2_000,
        expirationPolicy: 'CARRY_FORWARD_CAPPED',
        carryForwardCap: 200,
        unitPrice: 100,
        discountPercent: 5,
        accountsPayableName: 'Northwind Accounts Payable',
        accountsPayableEmail: 'ap@northwind.example',
        purchaseOrderRequired: true,
        purchaseOrderNumber: 'NW-PO-48213',
        taxExempt: false,
        clientExecSponsor: 'Hannah Brooks',
        createdAt: at(now, -270),
      },
      history: [],
      requests: [],
    },
    salesforce: {
      accounts: [{ id: '001NW00000DEMO1AAA', name: 'Northwind Group', type: 'Customer', billing: 'Minneapolis, MN', parent: null, includesChildren: false, children: [] }],
      salesforceReachable: true,
    },
    messaging: {
      mission: 'Get the right goods to the right place, and treat every customer like a neighbor.',
      vision: 'The distribution partner regional businesses trust most.',
      coreValues: ['Show up for each other', 'Own the outcome', 'Keep it simple', 'Leave it better than you found it'],
      csrPriorities: ['Youth programs near our warehouses', 'Support for veterans and their families', 'Hunger relief'],
    },
    inquiries: [
      {
        id: idFor('6e1a', 1),
        reference: 'NW-L-2701',
        status: 'IN_REVIEW',
        eventName: 'Sales Kickoff 2027',
        eventType: 'Sales meeting',
        estimatedParticipants: 220,
        budgetRange: '$40,000 to $60,000',
        decisionTimeline: 'Within a month',
        purpose: 'Launch the 2027 plan with every seller in one room.',
        locations: ['Minneapolis, MN'],
        createdAt: at(now, -12),
      },
    ],
    concierge: [
      {
        id: idFor('6e1b', 1),
        topic: 'CREDITS_AND_BILLING',
        subject: 'Can Sales lend us credits for the Veterans Day event?',
        body: 'Our Charlotte event is bigger than Community Impact’s allocation. Can credits move from the Sales allocation, or does it have to come from the shared pool?',
        status: 'ANSWERED',
        response:
          'Yes. An org admin can move credits between teams from the Credits page, and the expiry dates come with them. We held the Charlotte date against the shared pool for now, so nothing needs to move unless you want it to.',
        respondedAt: at(now, -7),
        respondedBy: 'Morgan Reyes',
        createdAt: at(now, -8),
        requester: { name: 'Alicia Moreno', email: 'alicia.moreno@northwind.example' },
      },
      {
        id: idFor('6e1b', 2),
        topic: 'PROGRAM_QUESTION',
        subject: 'Can Gingerbread Architect run in the warehouse break room?',
        body: 'The break room has long tables but no carpet and it gets cold. Is that a problem for the program?',
        status: 'OPEN',
        response: null,
        respondedAt: null,
        respondedBy: null,
        createdAt: at(now, -1),
        requester: { name: 'Ben Whitaker', email: 'ben.whitaker@northwind.example' },
      },
    ],
    topics: TOPICS.map((topic, index) => ({ ...topic, id: topicId(index), slug: topic.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'), sortOrder: index, archived: false, resourceCount: 0 })),
    resources: resources(now),
    banners: [
      banner(1, {
        kind: 'SEASONAL',
        eyebrow: 'GIVING SEASON',
        headline: 'Plan your year-end give-back now',
        body: 'Charity builds in November and December book up first. Pick a date early and your team gets its first choice of program.',
        ctaLabel: 'See charity programs',
        ctaHref: '/programs',
        audience: 'ORGANIZATIONS',
        organizations: northwindOnly,
        startsOn: day(now, -10),
        endsOn: day(now, 60),
      }),
      banner(2, {
        kind: 'COMPANY_MARKER',
        eyebrow: 'FOUNDERS WEEK',
        headline: 'Founders Week is coming to every office',
        body: 'Office leads: your plan and checklist are on the campaign page.',
        ctaLabel: 'Open the campaign',
        ctaHref: '/campaigns',
        audience: 'ORGANIZATIONS',
        organizations: northwindOnly,
        startsOn: day(now, -5),
        endsOn: day(now, 46),
      }),
      banner(3, {
        kind: 'EXPIRING_CREDITS',
        eyebrow: 'USE THEM OR LOSE THEM',
        headline: 'Credits from this term expire at the end of it',
        body: 'Book a program or move credits between teams before the term ends.',
        ctaLabel: 'See your credits',
        ctaHref: '/credits',
        audience: 'ALL_PORTALS',
        organizations: [],
        startsOn: day(now, -2),
        endsOn: day(now, 40),
      }),
      banner(4, {
        kind: 'LARGE_EVENT',
        eyebrow: 'PLAN AHEAD',
        headline: 'Planning a 2027 kickoff? Start the conversation now',
        body: 'Large meetings book venues months out. Tell BCE what you have in mind.',
        ctaLabel: 'Plan a large event',
        ctaHref: '/large-events',
        audience: 'ALL_PORTALS',
        organizations: [],
        startsOn: day(now, 14),
        endsOn: day(now, 75),
      }),
      banner(5, {
        kind: 'NATIONAL_OBSERVANCE',
        eyebrow: 'VOLUNTEER MONTH',
        headline: 'April is National Volunteer Month',
        body: 'Give your teams a day to build something for a local charity.',
        ctaLabel: '',
        ctaHref: '',
        audience: 'ALL_PORTALS',
        organizations: [],
        startsOn: day(now, -200),
        endsOn: day(now, -170),
      }),
    ],
    programs,
    pairings: [
      pairing(1, 'Leadership meeting', 'Igniting Team Performance®', 'Morning workshop', 'Bike Build Donation®', 'Afternoon give-back', 'Start with a session leaders can apply on Monday, then finish the day building something together for a local charity.'),
      pairing(2, 'Annual sales meeting', 'Speed Networking', 'Opening night', 'Best Corporate Feud', 'Closing session', 'Speed Networking mixes new and tenured sellers on the first night. Feud gives the meeting a loud, shared finish.'),
      pairing(3, 'Company celebration', 'Food Truck Face-Off', 'Main event', 'Bears and Blankets', 'Give-back station', 'Teams compete on the main floor while a give-back station runs alongside, so everyone can take part in their own way.'),
    ],
    seasonTags: [
      tag(1, 'Holiday season', [11, 12], 'Programs for end-of-year parties and celebrations.', ['Gingerbread Architect', 'Best Corporate Feud', 'Bears and Blankets']),
      tag(2, 'Giving season', [11, 12], 'Charity builds for the time of year teams most want to give back.', ['Bike Build Donation®', 'Operation Military Care', 'Bears and Blankets']),
      tag(3, 'New year kickoff', [1, 2], 'Programs that start the year with energy and new connections.', ['Speed Networking', 'Igniting Team Performance®', 'Competition to Collaboration®']),
      tag(4, 'Summer outings', [6, 7, 8], 'Active programs for warm-weather team days.', ['Team Olympics', 'Amazing Chase SmartHunt®', 'Food Truck Face-Off']),
    ],
    features: features(now),
    eventId: PORTAL_EVENT_ID,
    events: [bikeBuild(now)],
  };
}
